import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { firstValueFrom } from 'rxjs';
import { environment } from '../environments/environments';

export interface ChatMessage {
  role: 'user' | 'assistant';
  content: string;
  warnings?: string[];
  insights?: string[];
  followUpSuggestions?: string[];
  visualization?: VisualizationPayload;
  timestamp: Date;
  isLoading?: boolean;
  toolsUsed?: string[];
  plannerTokenUsage?: TokenUsage;
  answerTokenUsage?: TokenUsage;
}

export interface ChatRequest {
  query: string;
  conversationHistory: { role: string; content: string }[];
}

export interface ChatResponse {
  answer: string;
  warnings: string[];
  insights: string[];
  followUpSuggestions: string[];
  visualization?: VisualizationPayload;
  statusCode: number;
  statusMessage: string;
  toolsUsed?: string[];
  clarificationNeeded?: boolean;
  clarificationQuestion?: string;
  clarificationOptions?: string[];
  plannerTokenUsage?: TokenUsage;
  answerTokenUsage?: TokenUsage;
}

export interface TokenUsage {
  prompt_tokens?: number;
  completion_tokens?: number;
  total_tokens?: number;
}

export interface VisualizationPayload {
  enabled: boolean;
  intent?: 'chart' | 'table' | 'disabled' | string;
  libraryHints?: {
    charts?: string;
    reportTable?: string;
  };
  reasoning?: string;
  importance?: 'low' | 'medium' | 'high' | string;
  metadata?: {
    generatedAtUtc?: string;
    sourceTables?: string[];
    rowCounts?: Record<string, number>;
    aggregationDetected?: boolean;
  };
  charts?: VisualizationChart[];
  report?: VisualizationReport | null;
}

export interface VisualizationChart {
  id: string;
  title?: string;
  type?: string;
  description?: string;
  echarts?: {
    option?: Record<string, any>;
  };
}

export interface VisualizationReport {
  title?: string;
  columns?: VisualizationColumn[];
  rows?: Record<string, any>[];
}

export interface VisualizationColumn {
  field: string;
  headerName?: string;
  type?: 'text' | 'number' | string;
}

@Injectable({ providedIn: 'root' })
export class ChatbotService {

  private functionUrl = environment.chatbotUrl;

  constructor(private http: HttpClient) {}

  async sendMessage(request: ChatRequest): Promise<ChatResponse> {
    console.log('Sending request to chatbot API:', request);
    return firstValueFrom(
      this.http.post<ChatResponse>(`${this.functionUrl}/api/chatbot/query`, request)
    );
  }
}
